import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { Filter, Grid3X3, List, Search, Users } from 'lucide-react';
import type { PaginatedResponse } from '@shared/api-contracts';

import { PublicShell } from '@/components/public-shell';
import { fetchJson } from '@/lib/queryClient';
import CreatorCard from '@/components/creator-card';
import { ComingSoonBanner } from '@/components/coming-soon-banner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useResponsiveViewport } from '@/hooks/useResponsiveViewport';
import { useRegionScope } from '@/hooks/useRegionScope';
import { AutoRedirect } from '@/components/auto-redirect';
import {
  buildBrowseHref,
  buildReturnToTarget,
  readCanonicalBrowseState,
  updateBrowseUrl,
  useBrowseScrollRestoration,
} from '@/lib/browse-state';

const LIMIT = 12;
type ViewMode = 'grid' | 'list';

interface CreatorRecord {
  id: string;
  displayName: string;
  creatorHeadline?: string | null;
  bio?: string | null;
  profileImageUrl?: string | null;
  isVerified?: boolean;
}

export default function RegionCreatorsBrowse() {
  const { regionSlug, localitySlug } = useParams<{ regionSlug: string; localitySlug?: string }>();
  const { region, locality, isLoading, isError } = useRegionScope(regionSlug, localitySlug);
  const { isPhone, isTablet, isDesktop } = useResponsiveViewport();
  const initialBrowseState = useMemo(() => readCanonicalBrowseState(window.location.search), []);

  const [query, setQuery] = useState(initialBrowseState.q ?? '');
  const [appliedQuery, setAppliedQuery] = useState(initialBrowseState.q ?? '');
  const [view, setView] = useState<ViewMode>(
    (initialBrowseState.view as ViewMode) ?? (isDesktop ? 'grid' : 'list')
  );
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (!initialBrowseState.view) {
      setView(isDesktop ? 'grid' : 'list');
    }
  }, [initialBrowseState.view, isDesktop]);

  const basePath =
    region && locality
      ? `/regions/${region.slug}/${locality.slug}`
      : region
        ? `/regions/${region.slug}`
        : '/';
  const apiBase =
    region && locality
      ? `/api/regions/${region.slug}/${locality.slug}`
      : region
        ? `/api/regions/${region.slug}`
        : '/api/regions';

  const browseState = useMemo(
    () => ({
      q: appliedQuery || undefined,
      sort: 'featured',
      view,
    }),
    [appliedQuery, view]
  );

  useEffect(() => {
    if (region) {
      updateBrowseUrl(window.location.pathname, browseState);
    }
  }, [browseState, region]);

  const currentSearch = buildBrowseHref(window.location.pathname, browseState).split('?')[1] ?? '';
  useBrowseScrollRestoration(
    window.location.pathname,
    currentSearch ? `?${currentSearch}` : '',
    !isLoading
  );

  const { data: response, isLoading: creatorsLoading } = useQuery<PaginatedResponse<CreatorRecord>>(
    {
      queryKey: ['region-creators-browse', region?.slug, locality?.slug, appliedQuery, page],
      queryFn: async () => {
        const params = new URLSearchParams({
          limit: String(LIMIT),
          page: String(page),
        });
        if (appliedQuery) {
          params.set('search', appliedQuery);
        }

        return fetchJson<PaginatedResponse<CreatorRecord>>(
          `${apiBase}/creators?${params.toString()}`
        );
      },
      enabled: !!region,
      staleTime: 1000 * 60 * 5,
    }
  );

  if (isLoading) {
    return (
      <PublicShell routeKey="creators">
        <div className="page-shell flex min-h-[60vh] items-center justify-center">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      </PublicShell>
    );
  }

  if (isError || !region) {
    return (
      <PublicShell routeKey="not-found">
        <div className="page-shell-narrow flex min-h-[60vh] flex-col items-center justify-center gap-4 text-center">
          <h1 className="text-4xl font-semibold">We couldn’t load creators for this geography.</h1>
          <AutoRedirect />
          <Link href="/">
            <Button className="rounded-full px-5">Choose another market</Button>
          </Link>
        </div>
      </PublicShell>
    );
  }

  const creators = response?.data ?? [];
  const total = response?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));
  const displayName = locality?.name ?? region.name;
  const isComingSoon = locality?.slug === 'bloomington';
  const showDesktopRail = !isPhone && !isTablet;
  const returnTo = encodeURIComponent(
    buildReturnToTarget(window.location.pathname, window.location.search)
  );

  const filterControl = (
    <div className="space-y-5">
      <div>
        <label className="editorial-kicker">Search creators</label>
        <div className="relative mt-2">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={event => setQuery(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                setAppliedQuery(query);
                setPage(1);
              }
            }}
            placeholder="Ceramist, photographer, bread baker..."
            className="touch-target rounded-full pl-10"
          />
        </div>
      </div>
      <div className="flex flex-wrap gap-3">
        <Button
          className="touch-target rounded-full px-5"
          onClick={() => {
            setAppliedQuery(query);
            setPage(1);
          }}
        >
          Apply
        </Button>
        <Button
          variant="outline"
          className="touch-target rounded-full px-5"
          onClick={() => {
            setQuery('');
            setAppliedQuery('');
            setPage(1);
          }}
        >
          Reset
        </Button>
      </div>
    </div>
  );

  return (
    <PublicShell routeKey="creators">
      <section className="hero-gradient border-b border-border/60">
        <div className="page-shell grid gap-8 py-12 lg:grid-cols-[1.1fr_0.9fr] lg:items-end">
          <div>
            <p className="editorial-kicker">
              {region.type === 'online'
                ? 'Independent online creators'
                : `Creators in ${displayName}`}
            </p>
            <h1 className="section-title mt-4">
              {region.type === 'online'
                ? 'Creators teaching live from anywhere.'
                : `Creators in ${displayName}`}
            </h1>
            <p className="section-copy mt-5 max-w-2xl">
              Meet the artists, makers, and practitioners who teach on Kalovate. Each creator has a
              profile page with their background, classes, and upcoming sessions.
            </p>
          </div>

          <Card className="glass-panel rounded-[2rem] border-border/60 shadow-lg">
            <CardContent className="space-y-4 p-6">
              <div className="flex flex-wrap items-center gap-2">
                <Badge className="rounded-full bg-primary/10 px-3 py-1 text-primary">
                  {total} creators
                </Badge>
                <Badge variant="outline" className="rounded-full px-3 py-1">
                  {view}
                </Badge>
              </div>
              <p className="text-sm leading-6 text-muted-foreground">
                Browse creator profiles to see their background, classes, and upcoming sessions —
                then book directly from their page.
              </p>
              <Link href="/creator/apply">
                <Button className="touch-target w-full rounded-full px-5 sm:w-auto">
                  Become a creator
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="page-section">
        <div className="page-shell grid gap-8 xl:grid-cols-[18rem_minmax(0,1fr)]">
          {showDesktopRail ? (
            <aside className="sticky top-[calc(var(--shell-header-height)+1.5rem)] h-fit rounded-[1.75rem] border border-border/70 bg-card p-5 shadow-sm">
              {filterControl}
            </aside>
          ) : null}

          <div className="min-w-0 space-y-5">
            {isComingSoon ? <ComingSoonBanner kind="creators" localityName={displayName} /> : null}

            <div className="flex flex-col gap-4 rounded-[1.75rem] border border-border/70 bg-card p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-wrap gap-2">
                {appliedQuery ? (
                  <button
                    type="button"
                    onClick={() => {
                      setAppliedQuery('');
                      setQuery('');
                      setPage(1);
                    }}
                    className="touch-target rounded-full border border-border/70 px-4 text-sm"
                  >
                    “{appliedQuery}” ×
                  </button>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Search by name, headline, or specialty.
                  </p>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {!showDesktopRail ? (
                  <Sheet>
                    <SheetTrigger asChild>
                      <Button variant="outline" className="touch-target rounded-full px-4">
                        <Filter className="mr-2 h-4 w-4" />
                        Search
                      </Button>
                    </SheetTrigger>
                    <SheetContent side="bottom" className="max-h-[80dvh] rounded-t-[2rem]">
                      <SheetHeader>
                        <SheetTitle>Find a creator</SheetTitle>
                      </SheetHeader>
                      <div className="mt-6">{filterControl}</div>
                    </SheetContent>
                  </Sheet>
                ) : null}

                <div className="inline-flex rounded-full border border-border/70 bg-muted/50 p-1">
                  <button
                    type="button"
                    onClick={() => setView('grid')}
                    className={`touch-target rounded-full px-3 ${view === 'grid' ? 'bg-background shadow-sm' : ''}`}
                  >
                    <Grid3X3 className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setView('list')}
                    className={`touch-target rounded-full px-3 ${view === 'list' ? 'bg-background shadow-sm' : ''}`}
                  >
                    <List className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            {creatorsLoading ? (
              <div className="overflow-x-hidden grid gap-5 grid-cols-[repeat(auto-fill,minmax(18rem,1fr))]">
                {Array.from({ length: 4 }).map((_, index) => (
                  <div
                    key={index}
                    className="h-64 animate-pulse rounded-[1.75rem] border border-border/70 bg-card"
                  />
                ))}
              </div>
            ) : creators.length === 0 && isComingSoon ? null : creators.length === 0 ? (
              <Card className="rounded-[1.75rem] border-border/70 shadow-sm">
                <CardContent className="space-y-4 p-6">
                  <div className="flex items-center gap-3">
                    <Users className="h-5 w-5 text-primary" />
                    <h2 className="text-2xl font-semibold">No creators match this search yet</h2>
                  </div>
                  <p className="text-sm leading-6 text-muted-foreground">
                    Try clearing your search or browsing classes from this area instead.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Button
                      className="rounded-full px-5"
                      onClick={() => {
                        setAppliedQuery('');
                        setQuery('');
                        setPage(1);
                      }}
                    >
                      Clear search
                    </Button>
                    <Link href={`${basePath}/classes`}>
                      <Button variant="outline" className="rounded-full px-5">
                        Browse classes
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div
                className={
                  view === 'grid'
                    ? 'overflow-x-hidden grid gap-5 grid-cols-[repeat(auto-fill,minmax(18rem,1fr))]'
                    : 'overflow-x-hidden grid gap-5'
                }
              >
                {creators.map(creator => (
                  <CreatorCard
                    key={creator.id}
                    id={creator.id}
                    displayName={creator.displayName}
                    creatorHeadline={creator.creatorHeadline ?? 'Creative educator'}
                    bio={creator.bio}
                    profileImageUrl={creator.profileImageUrl}
                    isVerified={creator.isVerified}
                    localityName={displayName}
                    variant={view === 'grid' ? (isDesktop ? 'comfortable' : 'compact') : 'expanded'}
                    detailHref={`/creators/${creator.id}?returnTo=${returnTo}`}
                  />
                ))}
              </div>
            )}

            {totalPages > 1 ? (
              <div className="flex items-center justify-between rounded-[1.5rem] border border-border/70 bg-card p-4 shadow-sm">
                <p className="text-sm text-muted-foreground">
                  Page {page} of {totalPages}
                </p>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="rounded-full px-5"
                    disabled={page <= 1}
                    onClick={() => setPage(previous => Math.max(1, previous - 1))}
                  >
                    Previous
                  </Button>
                  <Button
                    className="rounded-full px-5"
                    disabled={page >= totalPages}
                    onClick={() => setPage(previous => Math.min(totalPages, previous + 1))}
                  >
                    Next
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </section>
    </PublicShell>
  );
}
